<script lang="ts">
  import Modal from '../../ui/Modal.svelte';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { createLedSceneMutation, updateLedSceneMutation } from '$lib/mutations/led_scenes';
  import { toast } from '$lib/state/toast.svelte';
  import { getErrorMessage } from '$lib/api';
  import { getDeviceTypeLabel, stateLabel } from '$lib/utils/led_scene_index';
  import type { InputDeviceResponse } from '$lib/types/input_device';
  import type { InputKeyResponse } from '$lib/types/input_key';
  import type { LEDLoad } from '$lib/types/led_scene';

  type DeviceUi = InputDeviceResponse & { name?: string };

  let {
    open = false,
    device = null,
    keyId = null,
    eventType = 'on',
    existingSceneId = null,
    existingLoads = [],
    buttonLabel = '',
    onclose = undefined,
    onsaved = undefined
  }: {
    open?: boolean;
    device?: DeviceUi | null;
    keyId?: number | null;
    eventType?: 'on' | 'off';
    existingSceneId?: number | null;
    existingLoads?: LEDLoad[];
    buttonLabel?: string;
    onclose?: () => void;
    onsaved?: () => void;
  } = $props();

  const devices = inputDevicesQuery();
  const createScene = createLedSceneMutation();
  const updateScene = updateLedSceneMutation();

  const devicesLoaded = $derived(!devices.isPending && devices.data !== undefined);

  const triggerKey = $derived((device?.keys ?? []).find((k) => k.id === keyId) ?? null);

  const triggerActionId = $derived.by(() => {
    if (!triggerKey) return null;
    const action = triggerKey.actions?.find((a) => a.event_type === eventType);
    return action?.id ?? null;
  });

  const triggerDisplayName = $derived(
    buttonLabel?.trim() ||
      triggerKey?.name?.trim() ||
      (triggerKey ? `Key ${triggerKey.key_index}` : 'Button')
  );

  const triggerMeta = $derived(
    triggerKey
      ? `Address ${triggerKey.address} · Button ${triggerKey.key_index}`
      : ''
  );

  let selectedTargets = $state<Map<string, 0 | 1>>(new Map());
  let initializedFor = $state<string | null>(null);
  let deviceFilter = $state<string>('');

  const targetState = $derived((eventType === 'on' ? 1 : 0) as 0 | 1);
  const triggerStateLabel = $derived(stateLabel(targetState));

  const allTargetOptions = $derived.by(() => {
    const options: Array<{
      /** LED target identity (bus address + key index) — shared by keys on
       *  devices with duplicate addresses, so rows can share selection state. */
      id: string;
      /** Unique per device row — Svelte each keys must be unique even when
       *  two devices share an address (flagged as a unit issue). */
      uid: string;
      deviceId: number;
      deviceName: string;
      deviceType: string;
      key: InputKeyResponse;
      label: string;
    }> = [];
    for (const dev of (devices.data ?? []) as DeviceUi[]) {
      for (const key of dev.keys ?? []) {
        const keyLabel = key.name?.trim() ? key.name : `Key ${key.key_index}`;
        options.push({
          id: `${key.address}:${key.key_index}`,
          uid: `${dev.id}:${key.address}:${key.key_index}`,
          deviceId: dev.id,
          deviceName: dev.name ?? `Device ${dev.id}`,
          deviceType: getDeviceTypeLabel(dev),
          key,
          label: `${keyLabel} (addr ${key.address})`
        });
      }
    }
    return options.sort(
      (a, b) => a.key.address - b.key.address || a.key.key_index - b.key.key_index
    );
  });

  // Device filter, mirroring the scene dialog's room/zone/unit scope selector.
  const deviceFilterOptions = $derived(
    ((devices.data ?? []) as DeviceUi[])
      .filter((dev) => (dev.keys ?? []).length > 0)
      .map((dev) => ({
        id: dev.id,
        name: `${dev.name ?? `Device ${dev.id}`} · ${getDeviceTypeLabel(dev)}`
      }))
  );

  const visibleTargetOptions = $derived(
    deviceFilter
      ? allTargetOptions.filter((opt) => String(opt.deviceId) === deviceFilter)
      : allTargetOptions
  );

  $effect(() => {
    if (!open) {
      initializedFor = null;
      deviceFilter = '';
      return;
    }
    const key = `${keyId}:${eventType}:${existingSceneId}`;
    if (initializedFor !== key) {
      initializedFor = key;
      const next = new Map<string, 0 | 1>();
      for (const load of existingLoads) {
        const id = `${load.address}:${load.key_index}`;
        next.set(id, load.state === 1 ? 1 : 0);
      }
      selectedTargets = next;
    }
  });

  function isSelected(id: string): boolean {
    return selectedTargets.has(id);
  }

  function targetStateFor(id: string): 0 | 1 {
    return selectedTargets.get(id) ?? targetState;
  }

  function toggleTarget(id: string) {
    const next = new Map(selectedTargets);
    if (next.has(id)) next.delete(id);
    else next.set(id, targetState);
    selectedTargets = next;
  }

  function setTargetState(id: string, state: 0 | 1) {
    const next = new Map(selectedTargets);
    next.set(id, state);
    selectedTargets = next;
  }

  function setAllVisibleStates(state: 0 | 1) {
    const next = new Map(selectedTargets);
    for (const opt of visibleTargetOptions) next.set(opt.id, state);
    selectedTargets = next;
  }

  function clearAll() {
    selectedTargets = new Map();
  }

  const saving = $derived(createScene.isPending || updateScene.isPending);

  function buildLedLoads(): Omit<LEDLoad, 'id' | 'created_at' | 'updated_at' | 'led_scene'>[] {
    const loads: Omit<LEDLoad, 'id' | 'created_at' | 'updated_at' | 'led_scene'>[] = [];
    const seen = new Set<string>();
    for (const opt of allTargetOptions) {
      // One load per LED target — duplicate-address devices must not produce
      // duplicate led_loads entries for the same address/key pair.
      if (seen.has(opt.id)) continue;
      seen.add(opt.id);
      if (!selectedTargets.has(opt.id)) continue;
      loads.push({
        group_id: opt.key.group_id,
        address: opt.key.address,
        key_index: opt.key.key_index,
        state: selectedTargets.get(opt.id) ?? targetState
      });
    }
    return loads;
  }

  async function save() {
    if (!triggerKey) {
      toast.error('No button selected');
      return;
    }
    if (selectedTargets.size === 0) {
      toast.error('Select at least one LED target');
      return;
    }

    const led_loads = buildLedLoads();

    try {
      if (existingSceneId) {
        // An existing LED scene always has an InputAction already (scene or
        // LED-only) — triggerActionId is guaranteed set here.
        await updateScene.mutateAsync({
          id: existingSceneId,
          request: { input_action: triggerActionId!, led_loads }
        });
        toast.success('LED mapping updated');
      } else if (triggerActionId) {
        await createScene.mutateAsync({ input_action: triggerActionId, led_loads });
        toast.success('LED mapping created');
      } else {
        // No scene action programmed for this button/event yet — create LED
        // logic directly; the backend finds-or-creates an LED-only action.
        await createScene.mutateAsync({ key_id: keyId!, event_type: eventType, led_loads });
        toast.success('LED mapping created');
      }
      onsaved?.();
      onclose?.();
    } catch (error) {
      const msg = getErrorMessage(error);
      const errStr = error instanceof Error ? error.message : String(error);
      if (errStr.includes('UNIQUE') || errStr.includes('duplicate') || errStr.includes('already exists')) {
        toast.error(
          'A mapping already exists for this button. Please close and reopen this panel to refresh, then use the Change button.'
        );
      } else {
        toast.error(msg);
      }
    }
  }
</script>

<Modal {open} size="xl" onclose={() => onclose?.()}>
  {#snippet header()}
    <h3 class="text-base font-medium text-text-primary">
      {triggerDisplayName} · {eventType === 'on' ? 'On LED mapping' : 'Off LED mapping'}
    </h3>
    <p class="text-xs text-text-muted mt-0.5">
      Pick the button LEDs that mirror this action and set each one on or off. New selections default
      to {triggerStateLabel}.
    </p>
    {#if triggerMeta}
      <p class="font-mono text-2xs text-text-muted mt-0.5">{triggerMeta}</p>
    {/if}
  {/snippet}

  {#snippet children()}
    <div class="space-y-4">
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-2xs uppercase tracking-wide text-text-muted">Device</span>
        <select
          class="rounded border border-border-strong bg-bg-canvas px-2 py-1 text-sm text-text-primary"
          bind:value={deviceFilter}
        >
          <option value="">All devices</option>
          {#each deviceFilterOptions as opt (opt.id)}
            <option value={String(opt.id)}>{opt.name}</option>
          {/each}
        </select>
      </div>

      <div class="flex items-center gap-2">
        <button type="button" class="btn-secondary text-xs" onclick={() => setAllVisibleStates(1)}>
          All on
        </button>
        <button type="button" class="btn-secondary text-xs" onclick={() => setAllVisibleStates(0)}>
          All off
        </button>
        <button type="button" class="btn-secondary text-xs" onclick={clearAll}>Clear</button>
        <span class="text-xs text-text-muted ml-1">
          {selectedTargets.size} LED{selectedTargets.size === 1 ? '' : 's'} selected
          {#if deviceFilter && visibleTargetOptions.length > 0}
            ({visibleTargetOptions.filter((o) => selectedTargets.has(o.id)).length} in this device)
          {/if}
        </span>
      </div>

      {#if !devicesLoaded}
        <p class="text-sm text-text-muted">Loading device keys…</p>
      {:else if visibleTargetOptions.length === 0}
        <p class="text-sm text-text-muted">
          No input keys {deviceFilter ? 'on this device' : 'on this unit'}.
        </p>
      {:else}
        <div
          class="border border-border-default rounded divide-y divide-border-default max-h-72 overflow-y-auto"
        >
          {#each visibleTargetOptions as opt (opt.uid)}
            {@const selected = isSelected(opt.id)}
            {@const rowState = targetStateFor(opt.id)}
            <div class="flex items-center gap-3 px-3 py-2">
              <label class="flex flex-1 min-w-0 items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected}
                  onchange={() => toggleTarget(opt.id)}
                />
                <span class="flex-1 min-w-0 text-sm text-text-primary truncate">{opt.label}</span>
              </label>
              <span class="text-2xs text-text-muted">{opt.deviceName} · {opt.deviceType}</span>
              <div
                class="shrink-0 inline-flex rounded border border-border-default p-0.5 text-2xs font-medium {selected
                  ? ''
                  : 'opacity-40'}"
              >
                <button
                  type="button"
                  class="px-2.5 py-0.5 rounded transition-colors duration-100 {rowState === 1
                    ? 'bg-text-primary text-bg-base'
                    : 'text-text-secondary hover:text-text-primary'}"
                  aria-pressed={rowState === 1}
                  disabled={!selected}
                  onclick={() => setTargetState(opt.id, 1)}
                >
                  On
                </button>
                <button
                  type="button"
                  class="px-2.5 py-0.5 rounded transition-colors duration-100 {rowState === 0
                    ? 'bg-text-primary text-bg-base'
                    : 'text-text-secondary hover:text-text-primary'}"
                  aria-pressed={rowState === 0}
                  disabled={!selected}
                  onclick={() => setTargetState(opt.id, 0)}
                >
                  Off
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet footer()}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-secondary text-sm" onclick={() => onclose?.()}>
        Cancel
      </button>
      <button
        type="button"
        class="btn-primary text-sm"
        disabled={saving || selectedTargets.size === 0}
        onclick={save}
      >
        {saving ? 'Saving…' : existingSceneId ? 'Save mapping' : 'Create mapping'}
      </button>
    </div>
  {/snippet}
</Modal>
